import * as XLSX from 'xlsx';
import type {
  BenchmarkMetricKey,
  BenchmarkSummaryBasis,
  SflagaImprovementBenchmarkReport,
} from '../services/sflagaImprovementBenchmark';
import { getTopRankedLeaderGroup } from './sflagaImprovementBenchmarkWinnerCounts';
import {
  getSflagaImprovementBenchmarkConditionSignature,
  SFLAGA_IMPROVEMENT_BENCHMARK_SCHEMA_VERSION,
} from './sflagaImprovementBenchmarkSchema';

function formatTimestampForFilename(timestamp: string): string {
  return timestamp.replace(/[:.]/g, '-');
}

function buildFilename(
  report: SflagaImprovementBenchmarkReport,
  extension: 'json' | 'xlsx'
): string {
  const benchmarkCode = report.experiment.benchmarkCode ?? 'g';
  return `${benchmarkCode}_benchmark_${report.experiment.taskCount}tasks_${formatTimestampForFilename(
    report.generatedAt
  )}.${extension}`;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function formatBasisLabel(basis: BenchmarkSummaryBasis): string {
  return basis === 'median' ? 'Median' : 'Mean';
}

function getMetricValueLabel(metricKey: BenchmarkMetricKey): string {
  switch (metricKey) {
    case 'fitness':
      return 'Fitness';
    case 'energy':
      return 'Energy';
    case 'cost':
      return 'Cost';
    case 'responseTime':
      return 'Response Time';
    case 'executionTime':
      return 'Execution Time';
    default:
      return metricKey;
  }
}

function getLeaderNames(
  condition: SflagaImprovementBenchmarkReport['conditions'][number],
  metricKey: BenchmarkMetricKey,
  basis: BenchmarkSummaryBasis
): string {
  const leaders = getTopRankedLeaderGroup(condition, metricKey, basis).leaders;
  if (leaders.length > 0) {
    return leaders.map((leader) => leader.algorithmName).join(', ');
  }

  const fallback =
    basis === 'median'
      ? condition.bestAlgorithmsByMedian[metricKey]
      : condition.bestAlgorithmsByMean[metricKey];
  return fallback.algorithmName;
}

function getRunParameterSchedule(report: SflagaImprovementBenchmarkReport) {
  return (
    report.experiment.runParameters ??
    Array.from({ length: report.experiment.numberOfRuns }, (_, index) => ({
      runNumber: index + 1,
      sharedParameters: report.experiment.sharedParameters,
    }))
  );
}

function summarizeRunParameterValue<T extends string | number>(
  report: SflagaImprovementBenchmarkReport,
  selector: (
    value: NonNullable<SflagaImprovementBenchmarkReport['experiment']['runParameters']>[number]
  ) => T
): T | 'Varies per run' {
  const values = getRunParameterSchedule(report).map(selector);
  const firstValue = values[0];

  if (values.every((value) => value === firstValue)) {
    return firstValue;
  }

  return 'Varies per run';
}

export function downloadSflagaImprovementBenchmarkJson(
  report: SflagaImprovementBenchmarkReport
): void {
  const blob = new Blob([`${JSON.stringify(report, null, 2)}\n`], {
    type: 'application/json',
  });
  triggerBlobDownload(blob, buildFilename(report, 'json'));
}

export function exportSflagaImprovementBenchmarkToExcel(
  report: SflagaImprovementBenchmarkReport,
  basis: BenchmarkSummaryBasis
): void {
  const workbook = XLSX.utils.book_new();
  const benchmarkCode = report.experiment.benchmarkCode ?? 'g';
  const parameterMode = report.experiment.parameterMode ?? 'shared';
  const runParameterSchedule = getRunParameterSchedule(report);

  const overviewRows: Array<Record<string, string | number | boolean>> = [
    {
      Key: 'Generated At',
      Value: report.generatedAt,
    },
    {
      Key: 'Benchmark',
      Value: benchmarkCode.toUpperCase(),
    },
    {
      Key: 'Schema Version',
      Value:
        report.experiment.benchmarkSchemaVersion ??
        SFLAGA_IMPROVEMENT_BENCHMARK_SCHEMA_VERSION,
    },
    {
      Key: 'Task Count',
      Value: report.experiment.taskCount,
    },
    {
      Key: 'Runs',
      Value: report.experiment.numberOfRuns,
    },
    {
      Key: 'Matrix Seed',
      Value: report.experiment.matrixSeed,
    },
    {
      Key: 'Fitness Mode',
      Value: report.experiment.fitnessMode,
    },
    {
      Key: 'Parallel Execution',
      Value: report.experiment.parallelExecution ?? false,
    },
    {
      Key: 'Task Variation Enabled',
      Value: report.experiment.taskSpecificVariationEnabled,
    },
    {
      Key: 'Parameter Mode',
      Value: parameterMode,
    },
    ...(report.experiment.parameterSeed
      ? [
          {
            Key: 'Parameter Seed',
            Value: report.experiment.parameterSeed,
          },
        ]
      : []),
    {
      Key: 'Service Count',
      Value: report.experiment.serviceCount,
    },
    {
      Key: 'Condition Signature',
      Value: getSflagaImprovementBenchmarkConditionSignature(report),
    },
    {
      Key: 'Parameter Policy',
      Value: summarizeRunParameterValue(
        report,
        (run) => run.sharedParameters.parameterPolicy
      ),
    },
    {
      Key: 'Population Size',
      Value: summarizeRunParameterValue(
        report,
        (run) => run.sharedParameters.initialPopulation
      ),
    },
    {
      Key: 'Iterations',
      Value: summarizeRunParameterValue(report, (run) => run.sharedParameters.iterations),
    },
    {
      Key: 'Selection Rate',
      Value: summarizeRunParameterValue(
        report,
        (run) => run.sharedParameters.selectionRate
      ),
    },
    {
      Key: 'Crossover Rate',
      Value: summarizeRunParameterValue(
        report,
        (run) => run.sharedParameters.crossoverRate
      ),
    },
    {
      Key: 'Mutation Rate',
      Value: summarizeRunParameterValue(
        report,
        (run) => run.sharedParameters.mutationRate
      ),
    },
    {
      Key: 'Memeplex Count',
      Value: summarizeRunParameterValue(
        report,
        (run) => run.sharedParameters.memeplexCount
      ),
    },
    {
      Key: 'Timing Method',
      Value: report.diagnostics.timingMethod,
    },
    {
      Key: 'Measured Scope',
      Value: report.diagnostics.measuredScope,
    },
    {
      Key: 'Calculators Reset Per Algorithm',
      Value: report.diagnostics.resetsCalculatorsPerAlgorithm,
    },
    ...report.diagnostics.notes.map((note, index) => ({
      Key: `Note ${index + 1}`,
      Value: note,
    })),
  ];
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(overviewRows),
    'Overview'
  );

  const conditionsSheetRows = report.conditions.flatMap((condition) => {
    const fitnessLeaders = getTopRankedLeaderGroup(condition, 'fitness', basis);
    const energyLeaders = getTopRankedLeaderGroup(condition, 'energy', basis);
    const costLeaders = getTopRankedLeaderGroup(condition, 'cost', basis);
    const responseLeaders = getTopRankedLeaderGroup(condition, 'responseTime', basis);
    const executionLeaders = getTopRankedLeaderGroup(condition, 'executionTime', basis);

    return [
      {
        Condition: condition.label,
        EnabledEnhancements: condition.enabledEnhancementIds.join(', ') || 'None',
        SFLAGA_Fitness_Mean: condition.sflaga.fitness.mean,
        SFLAGA_Fitness_Median: condition.sflaga.fitness.median,
        SFLAGA_Energy_Mean: condition.sflaga.energy.mean,
        SFLAGA_Energy_Median: condition.sflaga.energy.median,
        SFLAGA_Cost_Mean: condition.sflaga.cost.mean,
        SFLAGA_Cost_Median: condition.sflaga.cost.median,
        SFLAGA_ResponseTime_Mean: condition.sflaga.responseTime.mean,
        SFLAGA_ResponseTime_Median: condition.sflaga.responseTime.median,
        SFLAGA_ExecutionTime_Mean: condition.sflaga.executionTime.mean,
        SFLAGA_ExecutionTime_Median: condition.sflaga.executionTime.median,
        Fitness_Winner: getLeaderNames(condition, 'fitness', basis),
        Fitness_Tied: fitnessLeaders.isTie,
        Fitness_Margin: fitnessLeaders.marginToNextDistinct ?? '',
        Energy_Winner: getLeaderNames(condition, 'energy', basis),
        Energy_Tied: energyLeaders.isTie,
        Energy_Margin: energyLeaders.marginToNextDistinct ?? '',
        Cost_Winner: getLeaderNames(condition, 'cost', basis),
        Cost_Tied: costLeaders.isTie,
        Cost_Margin: costLeaders.marginToNextDistinct ?? '',
        ResponseTime_Winner: getLeaderNames(condition, 'responseTime', basis),
        ResponseTime_Tied: responseLeaders.isTie,
        ResponseTime_Margin: responseLeaders.marginToNextDistinct ?? '',
        ExecutionTime_Winner: getLeaderNames(condition, 'executionTime', basis),
        ExecutionTime_Tied: executionLeaders.isTie,
        ExecutionTime_Margin: executionLeaders.marginToNextDistinct ?? '',
      },
    ];
  });
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(conditionsSheetRows),
    'Conditions'
  );

  const winnerCountRows = Object.entries(
    basis === 'median' ? report.winnerCountsByMedian.byMetric : report.winnerCountsByMean.byMetric
  ).flatMap(([metricKey, rows]) =>
    rows.map((row) => ({
      Metric: getMetricValueLabel(metricKey as BenchmarkMetricKey),
      Algorithm: row.algorithmName,
      Wins: row.winCount,
      Basis: formatBasisLabel(basis),
    }))
  );
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(winnerCountRows),
    'Winner Counts'
  );

  const runParameterRows = runParameterSchedule.map((run) => ({
    Run: run.runNumber,
    ParameterSeed: run.parameterSeed ?? '',
    ParameterPolicy: run.sharedParameters.parameterPolicy,
    PopulationSize: run.sharedParameters.initialPopulation,
    Iterations: run.sharedParameters.iterations,
    SelectionRate: run.sharedParameters.selectionRate,
    CrossoverRate: run.sharedParameters.crossoverRate,
    MutationRate: run.sharedParameters.mutationRate,
    MemeplexCount: run.sharedParameters.memeplexCount,
  }));
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(runParameterRows),
    'Run Parameters'
  );

  const runRows = report.conditions.flatMap((condition) =>
    condition.runs.flatMap((run) =>
      Object.values(run.algorithms)
        .filter((algorithm): algorithm is NonNullable<typeof algorithm> => Boolean(algorithm))
        .map((algorithm) => ({
          Condition: condition.label,
          Run: run.runNumber,
          RunSeed: run.runSeed,
          ParameterSeed: run.parameterSeed ?? '',
          PopulationSize:
            run.sharedParameters?.initialPopulation ?? report.experiment.sharedParameters.initialPopulation,
          Iterations:
            run.sharedParameters?.iterations ?? report.experiment.sharedParameters.iterations,
          SelectionRate:
            run.sharedParameters?.selectionRate ?? report.experiment.sharedParameters.selectionRate,
          CrossoverRate:
            run.sharedParameters?.crossoverRate ?? report.experiment.sharedParameters.crossoverRate,
          MutationRate:
            run.sharedParameters?.mutationRate ?? report.experiment.sharedParameters.mutationRate,
          MemeplexCount:
            run.sharedParameters?.memeplexCount ?? report.experiment.sharedParameters.memeplexCount,
          Algorithm: algorithm.algorithmName,
          Fitness: algorithm.fitness,
          Energy: algorithm.energy,
          Cost: algorithm.cost,
          ResponseTime: algorithm.responseTime,
          ExecutionTime: algorithm.executionTime,
          FitnessEvaluations: algorithm.operations.fitnessEvaluations,
          CrossoverCalls: algorithm.operations.crossoverCalls,
          MutationCalls: algorithm.operations.mutationCalls,
          LocalSearchCalls: algorithm.operations.localSearchCalls,
        }))
    )
  );
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(runRows),
    'Runs'
  );

  const convergenceRows = report.conditions.flatMap((condition) =>
    Object.entries(condition.convergenceByAlgorithm).flatMap(
      ([algorithmKey, points]) =>
        (points ?? []).map((point) => ({
          Condition: condition.label,
          Algorithm: algorithmKey,
          Generation: point.generation,
          MeanFitness: point.mean,
          MedianFitness: point.median,
          MinFitness: point.min,
          MaxFitness: point.max,
        }))
    )
  );
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(convergenceRows),
    'Convergence'
  );

  const operationRows = report.conditions.flatMap((condition) =>
    Object.entries(condition.operationStatistics).map(([algorithmKey, stats]) => ({
      Condition: condition.label,
      Algorithm: algorithmKey,
      FitnessEvaluations_Mean: stats?.fitnessEvaluations.mean ?? '',
      FitnessEvaluations_Median: stats?.fitnessEvaluations.median ?? '',
      CrossoverCalls_Mean: stats?.crossoverCalls.mean ?? '',
      CrossoverCalls_Median: stats?.crossoverCalls.median ?? '',
      MutationCalls_Mean: stats?.mutationCalls.mean ?? '',
      MutationCalls_Median: stats?.mutationCalls.median ?? '',
      LocalSearchCalls_Mean: stats?.localSearchCalls.mean ?? '',
      LocalSearchCalls_Median: stats?.localSearchCalls.median ?? '',
    }))
  );
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(operationRows),
    'Operations'
  );

  XLSX.writeFile(workbook, buildFilename(report, 'xlsx'));
}
